import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/safeClient';
import { useAuth } from '@/contexts/AuthContext';

// =============================================================================
// Policy version — bump this string whenever your privacy policy or cookie
// policy changes materially. Logged-in users whose last server-recorded consent
// is for an older version will be asked to re-consent.
//
// Format: YYYY-MM  (one bump per calendar month is enough for most products)
// =============================================================================
export const CURRENT_POLICY_VERSION = '2026-04';

// localStorage key — version suffix avoids collisions with old data shapes
const CONSENT_KEY = 'cookie_consent_v2';

// =============================================================================
// Types
// =============================================================================

export interface CookieConsent {
  essential: true;
  maps: boolean;
  timestamp: string;
  policyVersion: string;
}

type ConsentSource = 'banner' | 'settings' | 'signup' | 'sync' | 'app';

// =============================================================================
// localStorage helpers
// =============================================================================

function readLocalConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsent;
  } catch {
    return null;
  }
}

function writeLocalConsent(maps: boolean): CookieConsent {
  const state: CookieConsent = {
    essential: true,
    maps,
    timestamp: new Date().toISOString(),
    policyVersion: CURRENT_POLICY_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  } catch {
    // Storage quota exceeded — UI still works from in-memory state
  }
  return state;
}

function clearLocalConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {}
}

// =============================================================================
// Supabase helpers
// =============================================================================

interface ServerConsentState {
  maps: boolean;
  policyVersion: string;
}

// Returns the user's latest consent state for CURRENT_POLICY_VERSION, or null
// if no record exists (user has never consented under this version).
async function fetchServerConsent(userId: string): Promise<ServerConsentState | null> {
  const { data, error } = await (supabase as any)
    .from('consent_log')
    .select('consent_type, granted, policy_version, created_at')
    .eq('user_id', userId)
    .eq('policy_version', CURRENT_POLICY_VERSION)
    .in('consent_type', ['essential', 'maps'])
    .order('created_at', { ascending: false });

  if (error || !data || data.length === 0) return null;

  // Derive latest decision per type (rows are DESC by created_at)
  const seen = new Set<string>();
  const latest: Record<string, boolean> = {};
  for (const row of data) {
    if (!seen.has(row.consent_type)) {
      seen.add(row.consent_type);
      latest[row.consent_type] = row.granted;
    }
  }

  // essential must be present — if missing the record is malformed
  if (!('essential' in latest)) return null;

  return { maps: latest['maps'] ?? false, policyVersion: CURRENT_POLICY_VERSION };
}

// Inserts two consent events: essential (always true) + maps.
// Silently returns false on error so callers can handle gracefully.
async function insertConsentEvents(
  userId: string,
  maps: boolean,
  source: ConsentSource,
): Promise<boolean> {
  const { error } = await (supabase as any)
    .from('consent_log')
    .insert([
      {
        user_id: userId,
        consent_type: 'essential',
        policy_version: CURRENT_POLICY_VERSION,
        granted: true,
        source,
      },
      {
        user_id: userId,
        consent_type: 'maps',
        policy_version: CURRENT_POLICY_VERSION,
        granted: maps,
        source,
      },
    ]);
  return !error;
}

// =============================================================================
// Hook
// =============================================================================

export function useCookieConsent() {
  const { user, loading: authLoading } = useAuth();

  // Initialise from localStorage for zero-flicker on first render
  const [consent, setConsent] = useState<CookieConsent | null>(readLocalConsent);
  // Becomes true once the server sync attempt (success or failure) has settled
  const [serverSynced, setServerSynced] = useState(false);

  // ===========================================================================
  // Server sync — runs once auth state is known and whenever the user changes
  // ===========================================================================
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // Anonymous visitor: localStorage is the only source of truth
      setServerSynced(false);
      return;
    }

    let cancelled = false;

    const syncWithServer = async () => {
      try {
        const serverState = await fetchServerConsent(user.id);

        if (cancelled) return;

        if (serverState) {
          // Server record exists for current policy — it is the source of truth.
          // Overwrite localStorage so the two stay in sync.
          const merged: CookieConsent = {
            essential: true,
            maps: serverState.maps,
            timestamp: new Date().toISOString(),
            policyVersion: serverState.policyVersion,
          };
          try { localStorage.setItem(CONSENT_KEY, JSON.stringify(merged)); } catch {}
          setConsent(merged);
        } else {
          // No server record for current policy version.
          const local = readLocalConsent();

          if (local?.policyVersion === CURRENT_POLICY_VERSION) {
            // User has valid local consent (e.g. they just logged in on a device
            // where they had already accepted). Sync it to the server so the
            // record is persisted — fire-and-forget, failure is non-fatal.
            insertConsentEvents(user.id, local.maps, 'sync').catch(() => {});
            // State is already correct from local; no need to setConsent()
          } else {
            // Either no local consent or an older policy version.
            // Clear stale data and require fresh consent.
            clearLocalConsent();
            setConsent(null);
          }
        }
      } catch {
        // Network / DB error: fall back to localStorage silently.
        // The banner may or may not appear depending on local state, which is
        // the safest degradation — we never silently skip showing the banner.
      } finally {
        if (!cancelled) setServerSynced(true);
      }
    };

    syncWithServer();
    return () => { cancelled = true; };
  }, [user?.id, authLoading]);

  // ===========================================================================
  // Save — writes to localStorage immediately; persists to server if logged in
  // ===========================================================================
  const save = useCallback((maps: boolean, source: ConsentSource = 'banner') => {
    const state = writeLocalConsent(maps);
    setConsent(state);

    if (user) {
      // Fire-and-forget: a failed server write does NOT roll back the local save.
      // The next sync (on next page load) will retry if needed.
      insertConsentEvents(user.id, maps, source).catch(() => {});
    }
  }, [user]);

  // ===========================================================================
  // Public API — backward-compatible with existing callers
  // ===========================================================================
  return {
    consent,

    // True only when the user has consented AND the record matches the current
    // policy version. Mismatched version = banner re-appears.
    hasConsented:
      consent !== null && consent.policyVersion === CURRENT_POLICY_VERSION,

    mapsAllowed: consent?.maps ?? false,

    acceptAll:          () => save(true,  'banner'),
    acceptEssentialOnly: () => save(false, 'banner'),
    enableMaps:         () => save(true,  'app'),

    // Exposed for settings/privacy pages that need to show sync status
    serverSynced,
  };
}
